import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useSearch } from '@tanstack/react-router'
import { AxiosError } from 'axios'
import { Drawer, DrawerWrapper, PageTitle } from '@/components'
import { ErrorState } from '@/components/common'
import { Details } from '@/components/molecules'
import { formatRelativeTimeAndDate } from '@/utils/formatRelativeTimeAndDate'
import { useReportsApi } from '@/hooks/useApi/useReportsApi/useReportsApi'
import { ReportWithDrawer } from '@/features/Reporting/components/ReportDrawer/ReportDrawer'
import { ReportDetailsActions } from '@/features/Reporting/components/ReportDetailsActions/ReportDetailsActions'
import { payablesSummarySearchSchema } from '@/schemas'
import { ReportHeader } from '@/features/Reporting/components/ReportHeader/ReportHeader'
import { Button } from '@/components/atoms'
import { useAppNavigation } from '@/hooks/useAppNavigation/useAppNavigation'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'
import { ReportRestrictedContent } from './ReportRestrictedContent'

type ReportDetailPageProps = {
    reportId: string
}

const ReportDetails: React.FC<ReportDetailPageProps> = ({ reportId }) => {
    const { useGetReport } = useReportsApi()
    const { goToEditReport } = useAppNavigation()

    const { data, isError, isSuccess, error } = useGetReport(reportId)
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = useState(false)

    const userId = useAuthStore((state) => state.user?.id) ?? ''

    const search = useSearch({
        from: '/_layout/reporting/$reportId',
    })

    const targetSearch =
        data?.type !== 'payables-summary'
            ? {}
            : payablesSummarySearchSchema.parse(search)

    if (isSuccess && JSON.stringify(targetSearch) !== JSON.stringify(search)) {
        return (
            <Navigate
                to={'/reporting/$reportId'}
                params={{ reportId }}
                search={targetSearch}
                replace={true}
            />
        )
    }

    if (isError) {
        const errorStatus =
            error instanceof AxiosError
                ? error.response?.status
                : 'status' in error && typeof error.status === 'number'
                  ? error.status
                  : null
        if (errorStatus === 403) {
            return (
                <div className="grid h-full px-6 pb-6">
                    <ReportRestrictedContent />
                </div>
            )
        }

        return (
            <div className="grid h-full px-6 pb-6">
                <ErrorState description={t('analytics.common.report_error')} />
            </div>
        )
    }

    if (!data) {
        return null
    }

    const { type, name, owner, access, updatedAt, filters, fields, settings } =
        data

    const isOwner = userId === data.owner.userId

    const reportDetails = [
        {
            label: t('reports.reportOwner'),
            value: owner.name,
        },
        {
            label: t('reports.reportAccess'),
            value: access,
        },
        {
            label: t('reports.reportLastUpdate'),
            value: formatRelativeTimeAndDate(updatedAt, true),
        },
    ]

    return (
        <div className="grid min-h-full grid-rows-[auto_1fr] gap-6">
            <ReportHeader
                backToPath={'/reporting'}
                backToLabel={t('reports.goBackToReporting')}
                title={name}
                renderTitle={() => (
                    <PageTitle
                        label={name}
                        labelTestId={'page-title-reporting-detail'}
                    />
                )}
            >
                <div className="flex gap-4">
                    {isOwner && (
                        <Button
                            name="submit"
                            variant={'outlinePrimary'}
                            size="large"
                            onClick={() => {
                                void goToEditReport(reportId)
                            }}
                            className="whitespace-nowrap"
                        >
                            {t('reports.customize')}
                        </Button>
                    )}

                    <ReportDetailsActions reportData={data} />
                </div>
            </ReportHeader>
            <DrawerWrapper className="h-full overflow-hidden border-t border-neutral-200">
                <Drawer
                    position="right"
                    className="h-[calc(100vh-11rem)]"
                    title={t('reports.reportDetailsTitle')}
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                >
                    <Details items={reportDetails} />
                </Drawer>
                <ReportWithDrawer
                    filters={filters}
                    dataSets={fields}
                    settings={settings}
                    type={type}
                    isOpen={isOpen}
                />
            </DrawerWrapper>
        </div>
    )
}

export default ReportDetails
